/**
 * Roster row kind/state — the customer/staff split (DNKADM-0360; dink-api handoff §4,
 * DNKAPI-0517).
 *
 * Every roster row now SAYS what it is (`kind`) instead of leaving the client to infer it
 * from `role`:
 *  - `kind: 'STAFF'` rows carry the staff ladder (`STAFF | ADMIN | OWNER`) and NEVER a
 *    linked CRM contact id;
 *  - `kind: 'CUSTOMER'` rows carry a `state` (`GUEST | USER`) and — only until dink-api's
 *    roster-source flip — a legacy `role`. After the flip a customer row carries **no
 *    role at all**, so nothing in the client may default a blank role to `'USER'`: that
 *    invents a role the wire never sent and silently misfiles people.
 *  - A row from before the flip (or from an endpoint that has not shipped 0517 yet) may
 *    carry NEITHER field — hence the role fallback below, kept for the whole window.
 *
 * A person who is both a customer and staff is **two rows with the same `userId`** — never
 * merged, never deduped — so a roster React key must be `userId` + `kind`, not `userId`.
 *
 * These rules live here rather than being mirrored per screen because "is this staff" now
 * has a two-field answer with a transition-window fallback, and three divergent copies of
 * that rule is exactly how a person gets misfiled. Nothing here is an authorization
 * decision: `kind` describes a row, it never gates a surface (that is the server's, and
 * the session's `tenant_role`).
 */

/** What a roster row IS. `null` when the row predates the field (see the module note). */
export type RosterKind = 'STAFF' | 'CUSTOMER' | null;

/** A customer row's account state. `null` on staff rows and on pre-0517 rows. */
export type CustomerState = 'GUEST' | 'USER' | null;

/** The `kind`/`state` pair every roster row carries, on both roster endpoints. */
export interface RosterRowKind {
  kind: RosterKind;
  state: CustomerState;
}

/**
 * Roles that mark a ROW as staff **when it carries no `kind`** — pre-flip data and any
 * endpoint that has not shipped its 0517 fields yet (and, on the Team & access screen,
 * an elevated invitation, which carries no `kind` at all). `STAFF` is the new floor role;
 * `SUPER_ADMIN` is no longer a storable tenant role (it lives only in platform-support
 * tokens) but stays in the set so an existing row still reads as staff rather than
 * silently becoming a customer.
 *
 * NOT an authorization set, and deliberately named apart from the two in
 * `@/auth/authContextValue`: `OPERATOR_ROLES` answers *may you enter* and `ADMIN_ROLES`
 * *may you configure* — for a SESSION. This one only asks what a roster ROW is, for
 * display. STAFF belongs here and must never be added to `ADMIN_ROLES` (DNKADM-0359).
 */
export const STAFF_ROSTER_ROLES: ReadonlySet<string> = new Set([
  'OWNER',
  'SUPER_ADMIN',
  'ADMIN',
  'STAFF',
]);

/**
 * A roster row's identity — `userId` + `kind`. `userId` alone is NOT unique since
 * DNKAPI-0517, so this is the React key for any roster list (and the key of any map that
 * stands in for a row, such as a screen's write-override map). Two rows, one person.
 */
export function rosterRowKey(row: { userId: number | string; kind: RosterKind }): string {
  return `${row.userId}:${row.kind}`;
}

/** `kind` by value — an unknown/absent wire value degrades to `null`, never to a guess. */
export function parseRosterKind(value: unknown): RosterKind {
  return value === 'STAFF' || value === 'CUSTOMER' ? value : null;
}

/** `state` by value — an unknown/absent wire value degrades to `null`, never to a guess. */
export function parseCustomerState(value: unknown): CustomerState {
  return value === 'GUEST' || value === 'USER' ? value : null;
}

/**
 * Is this roster row a staff login? `kind` when the row carries it, else the role set —
 * correct both before and after the flip. Display/filtering only: authorization is the
 * server's.
 */
export function isStaffRosterRow(row: RosterRowKind & { role: string }): boolean {
  if (row.kind !== null) return row.kind === 'STAFF';
  return STAFF_ROSTER_ROLES.has(row.role);
}

/**
 * The role value to DISPLAY for a roster row (the badge, the role column).
 *
 * The wire `role` when there is one; otherwise derived from what the row says it is: a
 * customer badges its `state` (`Guest`/`User`), a staff row with no role badges the floor
 * role `STAFF`. When neither field is known the answer is `''` — "unknown", which every
 * caller already renders as the placeholder. It is NEVER `'USER'` by default.
 */
export function rosterDisplayRole(row: RosterRowKind & { role?: string | null }): string {
  const role = row.role?.trim() ?? '';
  if (role.length > 0) return role;
  if (row.kind === 'CUSTOMER') return row.state ?? '';
  if (row.kind === 'STAFF') return 'STAFF';
  return '';
}
